// FILE: src/services/answerDistribution.js
//
// Answer-key position balance checks for authoring surfaces (Bulk Import,
// Question Factory batch tooling).
//
// ── Why this exists ───────────────────────────────────────────────────────────
//
//   A 2026-07-16 audit of the production banks found a strong positional bias
//   in authored correct answers (B is the key on 33% of PMP and >46% of CPMAI
//   single-select items; PMI-ACP/PMOCP are worse). Learners can exploit a
//   predictable key position, and it undermines exam realism. Runtime choice
//   shuffling was evaluated and REJECTED: ~88% of explanations reference
//   answer letters in free text, so re-lettering at display time risks
//   explanations that contradict the screen (see project memory
//   "answer-key-bias").
//
//   The accepted mitigation is authoring-side: surface the A/B/C/D key
//   distribution on every batch import and WARN (never block) when a position
//   is over-represented in a batch large enough for the signal to be
//   meaningful. Small imports vary naturally and are never warned on.
//
// ── Semantics ─────────────────────────────────────────────────────────────────
//
//   • Only SINGLE-SELECT multiple-choice rows count toward the distribution:
//     type 'multiple_choice' (or absent, the importer default) with exactly
//     one entry in correctAnswers. Multi-select keys are position-sets and
//     are not comparable.
//   • Choice ids are normalized to uppercase (the PMI-ACP bank uses
//     lowercase ids).
//   • The check is informational: callers render a warning, nothing more.
//
// Consumers: BulkImport.js (admin UI). scripts/validate-draft-batch.js
// implements the same rule standalone (it is CommonJS and dependency-free
// by design — keep the two in sync if semantics change).

/** Minimum single-select rows in a batch before over-representation warns. */
export const BALANCE_MIN_BATCH = 20;

/** A position whose share strictly exceeds this fraction triggers a warning. */
export const BALANCE_THRESHOLD = 0.35;

/**
 * Returns true when a raw question row participates in the single-select
 * answer-position distribution.
 *
 * @param {object} q - raw question (import shape or normalized shape)
 * @returns {boolean}
 */
export function isSingleSelectRow(q) {
  if (!q || typeof q !== 'object') return false;
  if (q.type && q.type !== 'multiple_choice') return false;
  if (!Array.isArray(q.choices) || q.choices.length < 2) return false;
  return Array.isArray(q.correctAnswers) && q.correctAnswers.length === 1;
}

/**
 * Computes the correct-answer position distribution for a batch.
 *
 * @param {object[]} questions - raw question rows
 * @returns {{ total: number, counts: Object<string, number> }}
 *   total  — number of single-select rows counted
 *   counts — uppercase position id → count (only positions that occur)
 */
export function computeAnswerDistribution(questions) {
  const counts = {};
  let total = 0;
  for (const q of Array.isArray(questions) ? questions : []) {
    if (!isSingleSelectRow(q)) continue;
    const pos = String(q.correctAnswers[0]).toUpperCase();
    counts[pos] = (counts[pos] || 0) + 1;
    total += 1;
  }
  return { total, counts };
}

/**
 * Evaluates a batch against the balance rule.
 *
 * @param {object[]} questions - raw question rows
 * @param {object}   [options]
 * @param {number}   [options.minBatch=BALANCE_MIN_BATCH]
 * @param {number}   [options.threshold=BALANCE_THRESHOLD]
 * @returns {{
 *   total: number,
 *   counts: Object<string, number>,
 *   shares: Object<string, number>,
 *   checked: boolean,
 *   warnings: Array<{ position: string, count: number, share: number }>,
 * }}
 *   checked  — false when the batch is below minBatch (distribution is still
 *              returned for display, but no warnings are produced)
 *   warnings — positions whose share strictly exceeds threshold, sorted by
 *              share descending
 */
export function evaluateAnswerBalance(questions, options = {}) {
  const minBatch  = options.minBatch  ?? BALANCE_MIN_BATCH;
  const threshold = options.threshold ?? BALANCE_THRESHOLD;

  const { total, counts } = computeAnswerDistribution(questions);
  const shares = {};
  for (const [pos, n] of Object.entries(counts)) {
    shares[pos] = total > 0 ? n / total : 0;
  }

  const checked = total >= minBatch;
  const warnings = !checked
    ? []
    : Object.entries(counts)
        .filter(([, n]) => n / total > threshold)
        .map(([position, count]) => ({ position, count, share: count / total }))
        .sort((a, b) => b.share - a.share);

  return { total, counts, shares, checked, warnings };
}

/**
 * Formats a distribution as a compact human-readable line,
 * e.g. "A: 5 (25%) · B: 6 (30%) · C: 5 (25%) · D: 4 (20%)".
 * Positions render in alphabetical order.
 *
 * @param {{ total: number, counts: Object<string, number> }} dist
 * @returns {string} empty string when there are no single-select rows
 */
export function formatDistributionLine(dist) {
  if (!dist || !dist.total) return '';
  return Object.keys(dist.counts)
    .sort()
    .map((pos) => {
      const n = dist.counts[pos];
      return `${pos}: ${n} (${Math.round((n / dist.total) * 100)}%)`;
    })
    .join(' · ');
}
